import {
  VuexModule,
  Module,
  Mutation,
  Action,
  getModule,
} from "vuex-module-decorators";
import store from "@/store";
import Firebase, { UserEntity, BoardEntity, AssetViewEntity } from "@/firebase";
import { initAllPageStates } from "@/libs/util";
import { AssetManagementModule } from "@/store/modules/net-finder/assetManagement";

@Module({ dynamic: true, store, name: "Auth", namespaced: true })
export class Auth extends VuexModule {
  user: { id: string; data: UserEntity } | null = null;
  boardList: { id: string; data: BoardEntity }[] | null = null;
  currentBoard: { id: string; data: BoardEntity } | null = null;
  error: string | null = null;
  loading: boolean = false;
  idToken: string | null = null;
  email: string | null = null;
  assetView: { id: string; data: AssetViewEntity } | null = null;

  get isAdminUser() {
    if (!this.user) {
      return false;
    }
    return this.user.data.admin;
  }

  get isSuperAdminUser() {
    if (!this.user) {
      return false;
    }
    return this.user.data.superAdmin;
  }

  get isSharedBoard() {
    if (!this.currentBoard) {
      return false;
    }
    return this.currentBoard.data.shared;
  }

  get vulnTrackerEnabled() {
    if (!this.currentBoard) {
      return false;
    }
    return !!this.currentBoard.data.services.vulnTracker;
  }

  get sysMonitorEnabled() {
    if (!this.currentBoard) {
      return false;
    }
    return !!this.currentBoard.data.services.sysMonitor;
  }

  get teleManagerEnabled() {
    if (!this.currentBoard) {
      return false;
    }
    return !!this.currentBoard.data.services.teleManager;
  }

  get userManagementEnabled() {
    return this.isSuperAdminUser;
  }

  get menuEnabled() {
    return this.isSuperAdminUser;
  }

  get sysMonitor() {
    if (!this.currentBoard) {
      return "";
    }
    return this.currentBoard.data.services.sysMonitor;
  }

  get isNetworkManager() {
    if (!this.user) {
      return false;
    }
    // superAdminをネットワーク管理権限ありとする
    return this.user.data.superAdmin;
  }

  // 権限管理（暫定）
  get allowCreateNetwork() {
    return this.isAdminUser || (this.isNetworkManager && !this.isSharedBoard);
  }

  get allowDeleteNetwork() {
    return this.isAdminUser || (this.isNetworkManager && !this.isSharedBoard);
  }

  @Mutation
  SetUser(payload: { id: string; data: UserEntity } | null) {
    this.user = payload;
  }

  @Mutation
  SetBoardList(payload: { id: string; data: BoardEntity }[] | null) {
    this.boardList = payload;
  }

  @Mutation
  SetCurrentBoard(payload: { id: string }) {
    if (!this.boardList || this.boardList.length < 1) {
      this.currentBoard = null;
      return;
    }
    const item = this.boardList.find((item) => item.id === payload.id);
    this.currentBoard = item || this.boardList[0];
    // ボード選択時に、資産管理情報のビュー状態を初期化する
    Firebase.fetchAssetViews(
      this.user != null && this.user.data.admin,
      this.currentBoard.data.customerId,
    ).then((result: { id: string; data: AssetViewEntity }[] | null) => {
      this.assetView = result != null ? result[0] : null;
      AssetManagementModule.initViews();
    });
  }

  @Mutation
  SetError(payload: string | null) {
    this.error = payload;
  }

  @Mutation
  SetLoading(payload: boolean) {
    this.loading = payload;
  }

  @Mutation
  SetIdToken(payload: string | null) {
    this.idToken = payload;
  }

  @Mutation
  SetEmail(payload: string | null) {
    this.email = payload;
  }

  @Action({ rawError: true })
  async fetchUser() {
    this.SetUser(
      await Firebase.fetchUser().catch((err) => {
        this.SetError(err);
        return null;
      }),
    );
  }

  @Action({ rawError: true })
  async fetchBoardList() {
    this.SetBoardList(
      await Firebase.fetchBoards()
        .then((result: { id: string; data: BoardEntity }[] | null) => {
          if (result != null) {
            result.sort((a, b) => {
              const nameA = a.data.name;
              const nameB = b.data.name;
              if (nameA < nameB) {
                return -1;
              }
              if (nameA > nameB) {
                return 1;
              }
              return 0;
            });
          }
          return result;
        })
        .catch((err) => {
          this.SetError(err);
          return null;
        }),
    );
  }

  @Action({ rawError: true })
  async getIdToken() {
    // eslint-disable-next-line
    const firebaseUser = await Firebase.getFirebaseUser().catch((_err) => null);
    if (!firebaseUser) return;
    // eslint-disable-next-line
    this.SetIdToken(await firebaseUser.getIdToken().catch((_err) => null));
  }

  @Action({ rawError: true })
  async login(payload: { email: string; password: string }) {
    this.SetLoading(true);
    this.SetError(null);
    await Firebase.signIn(payload.email, payload.password).catch((err) => {
      this.SetError(err);
    });
    this.SetLoading(false);
    // ログイン時にページ状態を初期化する
    initAllPageStates();
  }

  @Action({ rawError: true })
  async logout() {
    this.SetLoading(true);
    this.SetError(null);
    await Firebase.signOut().catch((err) => {
      this.SetError(err);
      return;
    });

    this.SetUser(null);
    this.SetBoardList(null);
    this.SetCurrentBoard({ id: "" });
    this.SetLoading(false);
    this.SetIdToken(null);
  }

  errorResetPassword: string | null = null;

  @Mutation
  SetErrorResetPassword(payload: string | null) {
    this.errorResetPassword = payload;
  }

  @Action({ rawError: true })
  async resetPassword(email: string) {
    this.SetErrorResetPassword(null);
    await Firebase.resetPassword(email).catch((err) => {
      this.SetErrorResetPassword(err);
    });
  }

  @Action({ rawError: true })
  async getEmail() {
    // eslint-disable-next-line
    const firebaseUser = await Firebase.getFirebaseUser().catch((_err) => null);
    if (!firebaseUser) return;
    this.SetEmail(firebaseUser.email);
  }

  errorChangePassword: string | null = null;

  @Mutation
  SetErrorChangePassword(payload: string | null) {
    this.errorChangePassword = payload;
  }

  @Action({ rawError: true })
  async changePassword(email: string) {
    this.SetErrorChangePassword(null);
    await Firebase.resetPassword(email).catch((err) => {
      this.SetErrorChangePassword(err);
    });
  }

  @Action({ rawError: true })
  async syncAssetView() {
    if (this.assetView) {
      Firebase.syncAssetView(this.assetView);
    }
  }
}

export const AuthModule = getModule(Auth);
